import { computed } from 'vue'
import { SECTION_LABELS, dateRange } from './sectionDefs.js'
import { sortByDate } from './resumeDates.js'

// What every layout needs from a resume: its header details, and the sections that have
// something in them, in the user's order, minus the hidden ones.

// http(s) addresses only become links, so a value like "javascript:..." can never be one.
const WEB = /^https?:\/\//i
const shown = (url) => url.replace(WEB, '').replace(/^www\./i, '').replace(/\/$/, '')

export const entryTitle = (key, entry) => ({
  experience: entry.title,
  education: [entry.degree, entry.field].filter(Boolean).join(', '),
  projects: entry.name,
  certifications: entry.name,
  custom: entry.title,
}[key] || '')

export const entrySubtitle = (key, entry) => ({
  experience: entry.company, education: entry.school, projects: entry.tech, certifications: entry.issuer, custom: entry.subtitle,
}[key] || '')

export const entryDate = (key, entry) => (['experience', 'education'].includes(key) ? dateRange(entry) : (entry.date || ''))

export function useResumeView(props) {
  const basics = computed(() => props.content.basics)
  const settings = computed(() => props.content.settings)
  const accent = computed(() => settings.value.accent)

  const contacts = computed(() => {
    const b = basics.value
    const plain = [b.email, b.phone, b.location].filter(Boolean).map((text) => ({ text }))
    const web = [b.website, b.linkedin, b.github].filter(Boolean).map((url) => ({ text: shown(url), href: WEB.test(url) ? url : null }))
    const more = b.links.filter((link) => link.url).map((link) => ({ text: link.label || shown(link.url), href: WEB.test(link.url) ? link.url : null }))
    return [...plain, ...web, ...more]
  })

  const sections = computed(() => {
    const { section_order: order, hidden_sections: hidden } = settings.value
    return order
      .filter((key) => !hidden.includes(key))
      .map((key) => {
        const custom = props.content.custom.find((section) => section.id === key)
        if (custom) return custom.entries.length ? { key: 'custom', id: key, title: custom.title || 'Untitled', entries: custom.entries } : null
        if (key === 'summary') return props.content.summary ? { key, id: key, title: SECTION_LABELS.summary } : null
        const entries = props.content[key] || []
        // Experience and education are always drawn latest first, whatever order they are stored in.
        return entries.length ? { key, id: key, title: SECTION_LABELS[key], entries: ['experience', 'education'].includes(key) ? sortByDate(entries) : entries } : null
      })
      .filter(Boolean)
  })

  const showPicture = computed(() => Boolean(props.pictureUrl) && settings.value.show_picture !== false)

  return { basics, settings, accent, contacts, sections, showPicture }
}
